"use client";

import { Button } from "@medimo/ui";
import { Megaphone, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useActiveBannerNotification } from "../hooks/use-active-banner-notification";
import { useNotificationBannerText } from "../hooks/use-notification-banner-text";

// 利用者がバナーを閉じた、お知らせのIDを記録する sessionStorage のキー。
// 値をbooleanではなくIDにしているのは、閉じたのと同じお知らせが表示対象であり続ける間だけ
// 非表示にするため。これにより、閉じた後に別の(IDが異なる)お知らせが新しく
// is_show_in_banner になった場合は気づける。
// sessionStorage はタブ単位で保持されるため、閉じた後は「同じタブを開いたまま」の間だけ
// 非表示になる(そのタブを閉じる、または新しいタブ・別ブラウザを開くと再表示される)。
// ログイン成功時にもこのキーをクリアする(apps/web/app/auth/sign-in/page.tsx 等参照)ため、
// 同じタブでも次回ログインでは再表示される。
// sessionStorage採用は意図的(採用理由・粒度はDD-2026-0920 セクション7「閉じる操作の現状の実装」参照)
export const NOTIFICATION_BANNER_DISMISSED_SESSION_KEY =
  "notificationTopBannerDismissed";
// 対象画面は診察・設定・お知らせ・AIチャットの4画面(DD-2026-0920 セクション3)。
// (dashboard)/layout.tsx はこれら以外の画面も内包するため、ここでパス側から絞る
const TARGET_PATH_PREFIXES = [
  "/consults",
  "/settings",
  "/notifications",
  "/chat",
];
// /consults のスマホ用レイアウト(consults/_layout.tsx の LayoutForSmartPhoneContent)は
// このバナーより外側(上)に位置するため、自身の高さを CSS 変数として公開し、
// 向こう側の fixed 位置の起点をずらしてもらう(--footer-container-h と同じ手法)
const BANNER_HEIGHT_CSS_VAR = "--notification-banner-h";

const NotificationTopBannerContent = () => {
  const notification = useActiveBannerNotification();

  // undefined: sessionStorageをまだ読んでいない(初期値、非表示側に倒す)。
  // null: 何も閉じていない。string: 閉じたお知らせのID。
  // sessionStorage は下の useEffect でしか読めないため、読み込み前から表示する
  // (undefinedを表示扱いにする)と「一瞬表示→即座に消える」チラつきが起きる
  // (既に閉じたはずの利用者に対して)。undefinedを非表示扱いにすれば、閉じていない
  // 利用者に一瞬遅れて表示されるだけで済む
  const [dismissedNotificationId, setDismissedNotificationId] = useState<
    string | null | undefined
  >(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      setDismissedNotificationId(
        sessionStorage.getItem(NOTIFICATION_BANNER_DISMISSED_SESSION_KEY),
      );
    } catch (e) {
      console.error("セッションストレージの読み込みに失敗しました:", e);
      // 読み込みに失敗した場合はバナーを表示したほうが気づいてもらえるため、
      // 「何も閉じていない」側に倒す
      setDismissedNotificationId(null);
    }
  }, []);

  const text = useNotificationBannerText(notification?.body ?? "");
  const isVisible =
    dismissedNotificationId !== undefined &&
    !!notification &&
    notification.id !== dismissedNotificationId &&
    text !== "";

  // 表示中は実際の高さを CSS 変数へ反映し、非表示になったら 0 に戻す。
  // 依存配列に isVisible を含めることで、表示/非表示が切り替わった直後の
  // (ref が付け外しされた後の)コミット後に測り直す
  // biome-ignore lint/correctness/useExhaustiveDependencies: isVisibleはコールバック内で直接参照していないが、表示/非表示切り替え直後(refの付け外し後)に高さを再計測するトリガーとして意図的に依存配列へ含めている
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      document.documentElement.style.setProperty(BANNER_HEIGHT_CSS_VAR, "0px");
      return;
    }
    const setHeightVar = () => {
      // offsetHeight は margin を含まないため、余白分もここで加算しないと
      // その分だけ外側のコンテナ高さが合わずスクロールが出てしまう
      const marginBottom =
        Number.parseFloat(getComputedStyle(el).marginBottom) || 0;
      document.documentElement.style.setProperty(
        BANNER_HEIGHT_CSS_VAR,
        `${el.offsetHeight + marginBottom}px`,
      );
    };
    setHeightVar();
    const ro = new window.ResizeObserver(setHeightVar);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [isVisible]);

  // アンマウント時(対象画面から離れた場合など)は変数自体を消す
  useEffect(() => {
    return () => {
      document.documentElement.style.removeProperty(BANNER_HEIGHT_CSS_VAR);
    };
  }, []);

  if (!isVisible) return null;

  const handleClose = () => {
    setDismissedNotificationId(notification.id);
    try {
      sessionStorage.setItem(
        NOTIFICATION_BANNER_DISMISSED_SESSION_KEY,
        notification.id,
      );
    } catch (e) {
      console.error("セッションストレージの書き込みに失敗しました:", e);
    }
  };

  return (
    <div
      ref={containerRef}
      // mt-1/md:mt-3・md:mx-3: DashboardDrawer側のスクロールコンテナが持つ周囲の余白
      // (py-1 md:p-3)を、このコンポーネント自身で再現する(スクロールコンテナの外に
      // 配置しているため、非表示時でも余白分の高さが残らないよう、container側ではなく
      // ここに持たせている)
      className="mt-1 mb-2 flex items-center gap-2 rounded bg-blue-50 px-4 py-2 text-blue-900 text-sm md:mx-3 md:mt-3"
    >
      <Megaphone className="h-5 w-5 shrink-0" aria-hidden />
      {/* line-clamp-2: 折り返しが多い場合でも高さを2行相当で頭打ちにする(Bizヒアリングにより確定) */}
      <Link
        href={`/notifications/${notification.id}`}
        className="line-clamp-2 min-w-0 flex-1 underline-offset-2 hover:underline"
      >
        {text}
      </Link>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={handleClose}
        aria-label="閉じる"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

// ログイン中の利用者にも、is_show_in_banner がONのお知らせを画面上部で届けるバナー(DD-2026-0920)。
// ログイン直後の通知モーダル(NotificationCoordinator)は Dialog の overlay 経由で
// このバナーより上に描画されるため、表示中かどうかを個別に判定する必要はない
export const NotificationTopBanner = () => {
  const pathname = usePathname();

  const isTargetPath = TARGET_PATH_PREFIXES.some((prefix) =>
    pathname?.startsWith(prefix),
  );
  if (!isTargetPath) return null;

  return <NotificationTopBannerContent />;
};
